import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Navbar from "@/components/dibs_landing_page/Navbar";
import Footer from "@/components/dibs_landing_page/Footer";
import { LinkedInIcon } from "@/components/admin_dashboard/BrandIcons";
import "./about-us.css";

const ROLLER_WORDS = [
  "admin work",
  "creating bookings",
  "chasing replies",
  "sending follow-ups",
  "updating records",
];

const CORE_VALUES = [
  {
    title: "Put customers first",
    body: "We start with the customer's problem, and measure ourselves by whether their day got easier.",
  },
  {
    title: "Move fast, learn faster",
    body: "We ship early, listen closely, and turn every result into the next improvement.",
  },
  {
    title: "Own the outcome",
    body: "We take responsibility for results, not just tasks, and follow through until the job is done.",
  },
  {
    title: "Keep it human",
    body: "Technology should make relationships warmer, not colder. Your team stays in control.",
  },
  {
    title: "Make it simple",
    body: "We cut steps, settings and jargon so the right thing is also the easy thing.",
  },
  {
    title: "Earn trust",
    body: "We are honest about what works and what doesn't, and we handle customer data with care.",
  },
];

const TEAM: {
  name: string;
  role: string;
  photo: string;
  quote: string;
  linkedin: string;
  zoom?: number;
}[] = [
  { name: "Yaojie Xiao", role: "Co-founder (Everything)", photo: "/dibs_assets/about/team-1-v2.webp", quote: "If it's not crazy, it's not worth doing.", linkedin: "https://www.linkedin.com/in/yaojiex" },
  { name: "Phoon Thet Pine", role: "Co-founder (Technology)", photo: "/dibs_assets/about/team-2-headshot.webp", quote: "Pressure is a privilege.", linkedin: "https://www.linkedin.com/in/thetpine/", zoom: 1.12 },
  { name: "Evan Tan", role: "Founding Engineer", photo: "/dibs_assets/about/team-3.webp", quote: "Never settle for less.", linkedin: "https://www.linkedin.com/in/evan-tan-3b0b59323/" },
  { name: "Roy Teo", role: "Founding Product Lead", photo: "/dibs_assets/about/team-4.webp", quote: "Dream it, then build it.", linkedin: "https://www.linkedin.com/in/roy-teo-jing-ming/" },
];

const PHOTOS = [
  {
    src: "/dibs_assets/about/sandbox-wall.jpg",
    alt: "The Dibs founders pointing at the Dibs logo on the Sandbox startup wall",
    position: "75% 50%",
  },
  {
    src: "/dibs_assets/about/team-group.jpg",
    alt: "The Dibs team smiling and giving thumbs up",
    position: "55% 50%",
  },
  {
    src: "/dibs_assets/about/team-working.jpg",
    alt: "A Dibs teammate working at a laptop with the app open",
    position: "50% 50%",
  },
];

const PhotoCarousel = () => {
  // Two identical halves so the track can loop seamlessly at -50%.
  const half = [...PHOTOS, ...PHOTOS];
  return (
    <div className="about-carousel" aria-label="Photos of the Dibs team">
      <div className="about-carousel__track">
        {[0, 1].map((copy) => (
          <div
            key={copy}
            className="about-carousel__half"
            aria-hidden={copy === 1}
          >
            {half.map((photo, i) => (
              <img
                key={i}
                src={photo.src}
                alt={copy === 0 ? photo.alt : ""}
                loading="lazy"
                className={`about-carousel__card ${
                  i % 2 === 0 ? "-rotate-2" : "rotate-2"
                }`}
                style={{ objectPosition: photo.position }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const ROLL_INTERVAL_MS = 2600;

const RollingWord = () => {
  const words = [...ROLLER_WORDS, ROLLER_WORDS[0]];
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [widths, setWidths] = useState<number[]>([]);
  const wordRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useLayoutEffect(() => {
    setWidths(wordRefs.current.map((el) => el?.offsetWidth ?? 0));
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const last = words.length - 1;
    let current = 0;
    let frame = 0;
    const timer = window.setInterval(() => {
      if (current < last) {
        current += 1;
        setAnimate(true);
        setIndex(current);
        return;
      }
      // Showing the duplicated first word: snap back to the real one, then roll on.
      current = 0;
      setAnimate(false);
      setIndex(0);
      frame = window.requestAnimationFrame(() => {
        frame = window.requestAnimationFrame(() => {
          current = 1;
          setAnimate(true);
          setIndex(1);
        });
      });
    }, ROLL_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
      window.cancelAnimationFrame(frame);
    };
  }, [words.length]);

  return (
    <span
      className="about-roller"
      aria-label={ROLLER_WORDS.join(", ")}
      style={widths[index] ? { width: widths[index] } : undefined}
    >
      <span
        className="about-roller__track"
        aria-hidden="true"
        data-animate={animate}
        style={{ transform: `translateY(calc(-${index} * 1.375em))` }}
      >
        {words.map((word, i) => (
          <span key={i} ref={(el) => (wordRefs.current[i] = el)}>
            {word}
          </span>
        ))}
      </span>
    </span>
  );
};

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-36 pb-56 sm:pt-44 sm:pb-64">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid gap-12 md:grid-cols-2 md:gap-16">
            <section>
              <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight mb-6">
                Our Mission
              </h1>
              <p className="text-lg sm:text-xl leading-snug text-foreground min-h-[4.125em]">
                <span className="block">
                  At Dibs, we want to help you spend less time on
                </span>
                <span className="block">
                  <RollingWord /> and more time on your customers.
                </span>
              </p>
            </section>
            <section>
              <h2 className="text-4xl sm:text-5xl font-heading font-bold tracking-tight mb-6">
                Our Belief
              </h2>
              <p className="text-lg sm:text-xl leading-snug text-foreground">
                Good service is personal. Dibs is built to bring every
                relationship into focus, one thoughtful next step at a time,
                instead of replacing the human touch.
              </p>
            </section>
          </div>
        </div>

        <div className="mt-16 sm:mt-20">
          <PhotoCarousel />
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-8">
        <section className="mt-24" aria-labelledby="about-values-title">
          <h2
            id="about-values-title"
            className="text-4xl sm:text-5xl font-heading font-bold tracking-tight mb-10"
          >
            Our Core Values
          </h2>
          <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CORE_VALUES.map((value, i) => (
              <li
                key={value.title}
                className="rounded-2xl border border-black/10 bg-white/90 p-6"
              >
                <span className="text-sm font-semibold tracking-widest text-primary">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-3 text-xl font-semibold text-foreground">
                  {value.title}
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">
                  {value.body}
                </p>
              </li>
            ))}
          </ol>
        </section>

          <section className="mt-24" aria-labelledby="about-team-title">
            <h2
              id="about-team-title"
              className="text-4xl sm:text-5xl font-heading font-bold tracking-tight mb-10"
            >
              Our Team
            </h2>
            <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {TEAM.map((member, i) => (
                <li key={i} tabIndex={0} className="group rounded-3xl outline-none">
                  <div className="relative aspect-[4/5] overflow-hidden rounded-3xl bg-muted">
                    <img
                      src={member.photo}
                      alt={`${member.name}, ${member.role}`}
                      loading="lazy"
                      className="h-full w-full object-cover object-bottom"
                      style={
                        member.zoom
                          ? { transform: `scale(${member.zoom})`, transformOrigin: "50% 30%" }
                          : undefined
                      }
                    />
                    <figure className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-3 bg-gradient-to-t from-black/75 via-black/45 to-transparent px-5 pb-5 pt-16 text-white opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus:translate-y-0 group-focus:opacity-100 motion-reduce:transition-none">
                      <blockquote className="text-lg font-medium leading-snug">
                        “{member.quote}”
                      </blockquote>
                    </figure>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">
                        {member.name}
                      </h3>
                      <p className="text-[15px] text-muted-foreground">
                        {member.role}
                      </p>
                    </div>
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${member.name} on LinkedIn`}
                      className="shrink-0 transition-opacity hover:opacity-80"
                    >
                      <LinkedInIcon size={22} />
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutUs;
